import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash, User2Icon } from 'lucide-react'
import Loader from '../../common/components/Loader'
import {
  Artist,
  ArtistPerformance,
  ArtistPerformanceResponse,
  Days,
  Stage,
} from '../../common/models'
import { IColumn, Table } from '../../core/components/Table'
import { dateToTimeOptions, formatDate } from '../../core/constants/times'
import { useTableParams } from '../../core/hooks/table-params'
import { useCheckPermission } from '../../core/hooks/use-check-permission'
import {
  deletePerformance,
  getAllArtists,
  getAllPerformances,
  getPerformance,
} from '../../core/services/artist'
import { getFestivalDays } from '../../core/services/dates-settings'
import { getStages } from '../../core/services/stage'
import { CreatePerfArtistModal } from './CreatePerfArtistModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import { Pagination } from './Pagination'
import { Icon } from '@/common/components/Icon'

export const StagePage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
  } = useTableParams({
    defaultSize: 50,
  })

  const { hasEditPermission } = useCheckPermission({ page: 'STAGES' })

  const params = useParams()
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [days, setDays] = useState<Days[]>()
  const [currentPerformance, setCurrentPerformance] =
    useState<ArtistPerformanceResponse>({} as ArtistPerformanceResponse)
  const [artists, setArtists] = useState<ArtistPerformance[]>([])
  const [allArtists, setAllArtists] = useState<Artist[]>([])
  const [stage, setStages] = useState<Stage>({} as Stage)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const { editionId, festivalName } = useParams()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const stageResponse = await getStages(params.stageId as string)
      const allDays = await getFestivalDays(params.editionId as string)
      const allArtistsResponse = await getAllArtists({ size: 1000 })
      setStages(stageResponse.data)
      setAllArtists(
        allArtistsResponse.data.content.sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }
          if (a.name > b.name) {
            return 1
          }
          return 0
        }),
      )
      setDays(allDays.data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const getAllPerf = useCallback(async () => {
    try {
      setLoading(true)
      const artistsResponse = await getAllPerformances({
        page,
        size,
        stageId: params.stageId,
        sortBy,
        sortDirection,
      })
      setArtists(artistsResponse.data.content)
      setTotalElemenets(artistsResponse.data.totalElements)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    params.stageId,
    sortBy,
    size,
    sortDirection,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getAllPerf()
  }, [getAllPerf])

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(artists.map((art) => art.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    )
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = () => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { status } = await deletePerformance(isCheck)
        getAllPerf()

        setIsCheck([])
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  if (!stage) {
    navigate(`/stages/${params.festivalName}/${params.editionId}`)
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onChange={handleSelectAll}
          disabled={!hasEditPermission}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
          disabled={!hasEditPermission}
        />
      ),
      width: '16px',
    },
    {
      title: 'Image',
      accessor: 'image',
      width: '66px',
      render: (row) => {
        return row.image ? (
          <img
            src={row.image}
            className="rounded-[10px] w-[40px] h-[40px] object-contain"
          />
        ) : (
          <div className="w-[40px] h-[40px] rounded-[10px] flex justify-center items-center font-size-20">
            <User2Icon className="w-4 h-4" />
          </div>
        )
      },
    },
    {
      title: 'Artist Name',
      accessor: 'artistName',
      sortable: true,
      render: (row) => row.artistName,
    },
    {
      title: 'Date',
      accessor: 'performanceDate',
      sortable: true,
      render: (row) => (
        <>
          <span>
            {row.performanceDate ? formatDate(row.performanceDate) : ''}{' '}
          </span>
          <span className="fw-bold">
            {row.festivalDayName ? row.festivalDayName : ''}
          </span>
        </>
      ),
    },
    {
      title: 'Start Time',
      accessor: 'startTime',
      sortable: true,
      render: (row) => {
        return row.startTime
          ? dateToTimeOptions(new Date(row.startTime || 0))
          : ''
      },
    },
    {
      title: 'End Time',
      accessor: '',
      render: (row) => {
        return row.endTime ? dateToTimeOptions(new Date(row.endTime || 0)) : ''
      },
    },

    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setShowCreateModal(true)
            setEditMode(true)
            const response = await getPerformance(row.id)
            setCurrentPerformance(response.data)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <Header
        pageTitle="Stages"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setShowCreateModal(true)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />

      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          backButton
          title="Stages"
          icon="stages-bold"
          className="mb-5"
          subtitle={stage?.translations?.[0]?.name}
        />
        <Table
          isCheck={isCheck}
          columns={columns}
          data={artists}
          rowReorder
          onRowReorder={(next) => setArtists(next)}
          handleSortBy={handleSortBy}
          alignment="middle"
          columnWidth="25%"
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreatePerfArtistModal
        editMode={editMode}
        stage={stage}
        performance={currentPerformance}
        artists={allArtists}
        setShowCreateModal={setShowCreateModal}
        showCreateModal={showCreateModal}
        setEditMode={setEditMode}
        page={page + 1}
        setArtists={setArtists}
        days={days}
        setLoading={setLoading}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Performance'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
